import * as React from 'react';

import type { LeadPipeline } from '../api';

/**
 * LeadFunnel — horizontal bars for the lead pipeline by stage (New → Open →
 * Approved → Converted). Bars are sized relative to the largest stage so the
 * shape reads as a funnel; each row shows the count and its share of all leads.
 *
 * Note: bars reflect *current* stage membership (the Lead record carries no
 * stage-history), so this is a distribution, not a cumulative drop-off.
 */
export function LeadFunnel({ pipeline }: { pipeline: LeadPipeline }) {
  const max = Math.max(1, ...pipeline.stages.map((s) => s.count));

  if (pipeline.total === 0) {
    return (
      <p className="tw-py-6 tw-text-center tw-text-sm tw-text-muted-foreground">
        No leads yet.
      </p>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      {pipeline.stages.map((s) => {
        const widthPct = Math.round((s.count / max) * 100);
        const sharePct =
          pipeline.total > 0 ? Math.round((s.count / pipeline.total) * 100) : 0;
        return (
          <div key={s.key} className="tw-flex tw-items-center tw-gap-3">
            <span className="tw-w-[78px] tw-shrink-0 tw-text-xs tw-font-semibold tw-text-muted-foreground">
              {s.label}
            </span>
            <div className="tw-relative tw-h-[30px] tw-flex-1 tw-overflow-hidden tw-rounded-md tw-bg-muted">
              <div
                className="tw-flex tw-h-full tw-items-center tw-rounded-md tw-pl-3 tw-text-xs tw-font-semibold tw-text-white tw-transition-all"
                style={{
                  width: `${Math.max(widthPct, s.count > 0 ? 12 : 0)}%`,
                  backgroundColor: s.color,
                }}
              >
                <span className="tw-tabular-nums">{s.count > 0 ? s.count : ''}</span>
              </div>
            </div>
            <span className="tw-w-[42px] tw-shrink-0 tw-text-right tw-text-[11px] tw-tabular-nums tw-text-muted-foreground">
              {sharePct}%
            </span>
          </div>
        );
      })}
      <div className="tw-mt-2 tw-flex tw-flex-wrap tw-gap-x-5 tw-gap-y-1 tw-border-t tw-border-border tw-pt-3 tw-text-[11.5px] tw-text-muted-foreground">
        <span>
          Conversion{' '}
          <b className="tw-text-success tw-tabular-nums">
            {Math.round(pipeline.conversionRate)}%
          </b>
        </span>
        <span>
          Churned <b className="tw-tabular-nums tw-text-foreground">{pipeline.churned}</b>
        </span>
        <span>
          Cancelled{' '}
          <b className="tw-tabular-nums tw-text-foreground">{pipeline.cancelled}</b>
        </span>
      </div>
    </div>
  );
}
